import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, MapPin, Users, BookOpen, Info } from 'lucide-react';
import { Button } from '../ui/Button';
import { motion, AnimatePresence } from 'framer-motion';
interface SubLink {
  name: string;
  path: string;
  external?: boolean;
  icon?: React.ReactNode;
  description?: string;
}
interface NavLink {
  name: string;
  path: string;
  subLinks?: SubLink[];
}
export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileSubMenuOpen, setMobileSubMenuOpen] = useState<string | null>(null);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setIsOpen(false);
    setOpenDropdown(null);
    setMobileSubMenuOpen(null);
  }, [location]);
  const navLinks: NavLink[] = [{
    name: 'HOME',
    path: '/'
  }, {
    name: 'ABOUT',
    path: '/about',
    subLinks: [{
      name: 'Our Story',
      path: '/about',
      description: 'Learn about our journey',
      icon: <Info className="w-4 h-4" data-id="element-157" />
    }, {
      name: 'Plan Your Visit',
      path: '/visit',
      description: 'What to expect',
      icon: <MapPin className="w-4 h-4" data-id="element-158" />
    }]
  }, {
    name: 'MINISTRIES',
    path: '/ministries',
    subLinks: [{
      name: 'Our Ministries',
      path: '/ministries',
      description: 'Get involved',
      icon: <Users className="w-4 h-4" data-id="element-159" />
    }, {
      name: 'What We Believe',
      path: '/what-we-believe',
      description: 'Our faith statement',
      icon: <BookOpen className="w-4 h-4" data-id="element-160" />
    }]
  }, {
    name: 'SERMONS',
    path: '/resources'
  }, {
    name: 'EVENTS',
    path: '/events'
  }, {
    name: 'GIVE',
    path: '/give'
  }, {
    name: 'CONTACT',
    path: '/contact'
  }];
  const isSubLinkActive = (link: NavLink) => {
    if (!link.subLinks) return false;
    return link.subLinks.some(sub => !sub.external && location.pathname === sub.path);
  };
  return <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-[#0A1F44] shadow-lg py-2' : 'bg-transparent py-6'}`} data-id="element-161">
      <div className="container-custom" data-id="element-162">
        <div className="flex flex-wrap items-center justify-between" data-id="element-163">
          {/* Logo */}
          <Link to="/" className={`flex items-center gap-3 md:gap-4 group z-10 transition-all duration-300 min-w-0 flex-shrink ${scrolled ? 'scale-90 origin-left' : ''}`} data-id="element-164">
            <img src="https://cdn.magicpatterns.com/uploads/7J3FrpjsQQtTFpuFCXMBr8/Mar_17__2026__04_26_25_PM-removebg-preview.png" alt="The Household Logo" className="w-10 h-10 md:w-14 md:h-14 rounded-full shrink-0 shadow-lg object-cover" data-id="element-165" />
            <div className="flex flex-col min-w-0" data-id="element-166">
              <span className="text-white font-serif text-sm sm:text-lg md:text-xl font-bold tracking-wide leading-tight truncate" data-id="element-167">
                HOUSEHOLD OF CHRIST CHURCH
              </span>
              <div className="flex items-center gap-2 text-white/80 text-[10px] sm:text-xs tracking-[0.2em] uppercase mt-0.5 md:mt-1" data-id="element-168">
                <div className="h-[1px] w-3 md:w-4 bg-[#C8102E]" data-id="element-169"></div>
                <span className="truncate" data-id="element-170">THE HOUSEHOLD</span>
                <div className="h-[1px] w-3 md:w-4 bg-[#C8102E]" data-id="element-171"></div>
                <span className="hidden sm:inline" data-id="element-172">GATEWAY OF HEAVEN</span>
              </div>
            </div>
          </Link>

          {/* Mobile Menu Button */}
          <button className="md:hidden p-2 text-white z-10 shrink-0 ml-auto" onClick={() => setIsOpen(!isOpen)} aria-label="Toggle menu" data-id="element-173">
            {isOpen ? <X className="w-6 h-6" data-id="element-174" /> : <Menu className="w-6 h-6" data-id="element-175" />}
          </button>

          {/* Desktop Navigation Links */}
          <div className={`hidden md:flex items-center justify-center gap-6 lg:gap-10 transition-all duration-300 ${scrolled ? 'order-none flex-1 px-4 xl:px-8' : 'order-last w-full mt-6'}`} data-id="element-176">
            {navLinks.map(link => link.subLinks ? <div key={link.name} className="relative" onMouseEnter={() => setOpenDropdown(link.name)} onMouseLeave={() => setOpenDropdown(null)} data-id="element-177">
                  <button className={`text-sm font-bold tracking-widest uppercase transition-colors hover:text-[#C8102E] flex items-center gap-1.5 py-2 ${isSubLinkActive(link) || openDropdown === link.name ? 'text-[#C8102E]' : 'text-white'}`} data-id="element-178">
                    {link.name}
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${openDropdown === link.name ? 'rotate-180' : ''}`} data-id="element-179" />
                  </button>

                  <AnimatePresence data-id="element-180">
                    {openDropdown === link.name && <motion.div initial={{
                opacity: 0,
                y: 8
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 8
              }} transition={{
                duration: 0.15,
                ease: 'easeOut'
              }} className="absolute top-full left-1/2 -translate-x-1/2 pt-3" data-id="element-181">
                        <div className="bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden min-w-[260px]" data-id="element-182">
                          <div className="h-1 bg-[#C8102E]" data-id="element-183" />
                          <div className="p-2" data-id="element-184">
                            {link.subLinks.map(subLink => <Link key={subLink.name} to={subLink.path} className={`flex items-start gap-3 px-4 py-3 rounded-lg transition-all hover:bg-[#F5F7FA] group ${location.pathname === subLink.path ? 'bg-[#F5F7FA]' : ''}`} data-id="element-185">
                                <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 transition-colors ${location.pathname === subLink.path ? 'bg-[#0A1F44] text-white' : 'bg-[#F5F7FA] text-[#0A1F44] group-hover:bg-[#0A1F44] group-hover:text-white'}`} data-id="element-186">
                                  {subLink.icon}
                                </span>
                                <div data-id="element-187">
                                  <span className={`block font-bold transition-colors ${location.pathname === subLink.path ? 'text-[#C8102E]' : 'text-[#0A1F44] group-hover:text-[#C8102E]'}`} data-id="element-188">
                                    {subLink.name}
                                  </span>
                                  {subLink.description && <span className="block text-xs text-gray-500 mt-0.5" data-id="element-189">
                                      {subLink.description}
                                    </span>}
                                </div>
                              </Link>)}
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div> : <Link key={link.name} to={link.path} className={`text-sm font-bold tracking-widest uppercase transition-colors hover:text-[#C8102E] py-2 relative ${location.pathname === link.path ? 'text-[#C8102E]' : 'text-white'}`} data-id="element-190">
                  {link.name}
                  {location.pathname === link.path && <motion.div layoutId="nav-indicator" className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#C8102E]" data-id="element-191" />}
                </Link>)}
          </div>

          {/* Desktop CTA */}
          <div className={`hidden md:block transition-all duration-300 z-10 ${scrolled ? 'order-none' : 'ml-auto'}`} data-id="element-192">
            <Link to="/visit" data-id="element-193">
              <Button variant="primary" size={scrolled ? 'sm' : 'md'} className="font-bold tracking-wider text-sm shadow-lg shadow-red-900/20" data-id="element-194">
                JOIN US THIS SUNDAY
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-195">
        {isOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="md:hidden bg-[#0A1F44] border-t border-white/10 shadow-xl" data-id="element-196">
            <div className="container-custom py-6 flex flex-col gap-1" data-id="element-197">
              {navLinks.map(link => link.subLinks ? <div key={link.name} data-id="element-198">
                    <button onClick={() => setMobileSubMenuOpen(mobileSubMenuOpen === link.name ? null : link.name)} className={`w-full text-sm tracking-widest font-bold px-4 py-3 rounded-lg flex items-center justify-between transition-colors ${isSubLinkActive(link) || mobileSubMenuOpen === link.name ? 'bg-white/10 text-[#C8102E]' : 'text-white hover:bg-white/5'}`} data-id="element-199">
                      {link.name}
                      <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${mobileSubMenuOpen === link.name ? 'rotate-180' : ''}`} data-id="element-200" />
                    </button>
                    <AnimatePresence data-id="element-201">
                      {mobileSubMenuOpen === link.name && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="overflow-hidden" data-id="element-202">
                          <div className="py-2 pl-4 space-y-1" data-id="element-203">
                            {link.subLinks.map(subLink => <Link key={subLink.name} to={subLink.path} className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${location.pathname === subLink.path ? 'bg-white/10 text-[#C8102E]' : 'text-white/80 hover:bg-white/5 hover:text-white'}`} data-id="element-204">
                                <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-white/10 text-white" data-id="element-205">
                                  {subLink.icon}
                                </span>
                                <span className="block font-bold text-sm" data-id="element-206">
                                  {subLink.name}
                                </span>
                              </Link>)}
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div> : <Link key={link.name} to={link.path} className={`text-sm tracking-widest font-bold px-4 py-3 rounded-lg flex items-center gap-2 transition-colors ${location.pathname === link.path ? 'bg-white/10 text-[#C8102E]' : 'text-white hover:bg-white/5'}`} data-id="element-207">
                    {link.name}
                  </Link>)}
              <div className="pt-6 mt-4 border-t border-white/10" data-id="element-208">
                <Link to="/visit" data-id="element-209">
                  <Button className="w-full" variant="primary" data-id="element-210">
                    JOIN US THIS SUNDAY
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </nav>;
}